import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import Topbar from '../../components/shared/Topbar';
import EmployeeSidebar from '../../components/employee/EmployeeSidebar';
import LegalFooter from '../../components/shared/LegalFooter';
import '../../styles/global.css';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const AUDIENCE_OPTIONS = [
  { value: 'all_tenants', label: 'All active tenants (entire platform)' },
  { value: 'property_tenants', label: 'All tenants at a specific property' },
  { value: 'landlord_tenants', label: 'All tenants under a specific landlord' },
  { value: 'no_payment_method', label: 'Tenants with no payment method on file' },
];

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [dripTemplates, setDripTemplates] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [sending, setSending] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('campaigns');

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm();
  const audienceType = watch('audience_type');
  const templateKey = watch('template_key');

  useEffect(() => {
    Promise.all([
      api.get('/campaigns'),
      api.get('/campaigns/templates'),
      api.get('/employee/landlords'),
      api.get('/campaigns/drip-templates').catch(() => ({ data: { templates: [] } })),
    ]).then(([campRes, tmplRes, llRes, dripRes]) => {
      setCampaigns(campRes.data.campaigns);
      setTemplates(tmplRes.data.templates);
      setLandlords(llRes.data.landlords);
      setDripTemplates(dripRes.data.templates || []);
    }).catch(() => toast.error('Could not load campaigns'))
      .finally(() => setLoading(false));
  }, []);

  // Load properties when landlord is selected
  useEffect(() => {
    if (audienceType === 'property_tenants') {
      api.get('/employee/landlords').then(res => {
        // Get all properties across all landlords for simplicity
        api.get('/admin/properties').then(pRes => setProperties(pRes.data.properties || [])).catch(() => {});
      });
    }
  }, [audienceType]);

  // Auto-fill template when selected
  useEffect(() => {
    if (templateKey) {
      const tmpl = templates.find(t => t.key === templateKey);
      if (tmpl && tmpl.key !== 'custom') {
        setValue('subject', tmpl.subject);
        setValue('body', tmpl.body);
        setSelectedTemplate(tmpl);
      }
    }
  }, [templateKey, templates, setValue]);

  const onSubmit = async (data) => {
    try {
      const payload = {
        name: data.name,
        template_key: data.template_key,
        subject: data.subject,
        body: data.body,
        audience_type: data.audience_type,
        audience_filter: {
          property_id: data.property_id || undefined,
          landlord_id: data.landlord_id || undefined,
        }
      };
      await api.post('/campaigns', payload);
      toast.success('Campaign created!');
      reset();
      setShowForm(false);
      const res = await api.get('/campaigns');
      setCampaigns(res.data.campaigns);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not create campaign');
    }
  };

  const sendCampaign = async (id) => {
    if (!window.confirm('Send this campaign now? This will email all recipients immediately.')) return;
    setSending(prev => ({ ...prev, [id]: true }));
    try {
      const res = await api.post(`/campaigns/${id}/send`);
      toast.success(`Campaign sent to ${res.data.sent} recipients!`);
      const campRes = await api.get('/campaigns');
      setCampaigns(campRes.data.campaigns);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send campaign');
    } finally {
      setSending(prev => ({ ...prev, [id]: false }));
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="app-layout">
      <Topbar role="employee" />
      <div className="dashboard-layout">
        <EmployeeSidebar />
        <main id="main-content" className="main-content">
          <div className="flex-between" style={{ marginBottom: 20 }}>
            <div>
              <div className="page-title">Email Campaigns</div>
              <div className="page-sub" style={{ margin: 0 }}>Create and send targeted emails to tenants.</div>
            </div>
            <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); reset(); }}>
              {showForm ? 'Cancel' : '+ New campaign'}
            </button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
            {[{ id: 'campaigns', label: 'Campaigns' }, { id: 'drip', label: 'Drip Templates' }].map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                background: 'none', border: 'none', padding: '10px 20px', cursor: 'pointer',
                fontSize: 14, fontWeight: 600,
                color: activeTab === t.id ? 'var(--navy)' : 'var(--text-sub)',
                borderBottom: activeTab === t.id ? '2px solid var(--navy)' : '2px solid transparent',
                marginBottom: -1,
              }}>{t.label}</button>
            ))}
          </div>

          {/* Drip Templates tab */}
          {activeTab === 'drip' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 13, color: 'var(--text-sub)', marginBottom: 4 }}>
                Pre-written email sequence for tenant outreach. Click "Use This Template" to clone into a new draft campaign.
              </div>
              {dripTemplates.map(dt => (
                <div key={dt.key} className="card">
                  <div className="card-header">
                    <div>
                      <div className="card-title">{dt.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-sub)', marginTop: 2 }}>Subject: {dt.subject}</div>
                    </div>
                    <button className="btn btn-primary btn-sm" onClick={() => {
                      setActiveTab('campaigns');
                      setShowForm(true);
                      reset();
                      setTimeout(() => {
                        setValue('name', dt.name + ' — ' + new Date().toLocaleDateString());
                        setValue('subject', dt.subject);
                        setValue('body', dt.body);
                      }, 50);
                    }}>Use This Template</button>
                  </div>
                  <div className="card-body">
                    <pre style={{ fontSize: 12, color: 'var(--text)', whiteSpace: 'pre-wrap', lineHeight: 1.6, fontFamily: 'inherit', margin: 0 }}>{dt.body}</pre>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'campaigns' && <>
          {/* Merge field reference */}
          <div style={{ background: '#f0f9ff', border: '1px solid var(--accent)', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--teal)', marginBottom: 6 }}>Available merge fields</div>
            <div style={{ fontSize: 11, color: 'var(--text-sub)', lineHeight: 1.8 }}>
              {['{{first_name}}', '{{property_name}}', '{{unit_number}}', '{{plan_name}}', '{{bulknet_price}}', '{{retail_price}}',
                '{{savings_per_month}}', '{{savings_per_year}}', '{{account_manager_name}}', '{{account_manager_email}}',
                '{{account_manager_phone}}', '{{login_url}}'].map(f => (
                <span key={f} style={{ display: 'inline-block', background: 'white', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 6px', margin: '2px', fontFamily: 'monospace' }}>{f}</span>
              ))}
            </div>
          </div>

          {/* New campaign form */}
          {showForm && (
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-header"><div className="card-title">Create new campaign</div></div>
              <div className="card-body">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="two-col">
                    <div className="form-group">
                      <label className="form-label">Campaign name</label>
                      <input className="form-input" placeholder="e.g. March Payment Reminder"
                        {...register('name', { required: true })} />
                      {errors.name && <div className="form-error">Required</div>}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Template</label>
                      <select className="form-input" {...register('template_key', { required: true })}>
                        <option value="">Select a template</option>
                        {templates.map(t => <option key={t.key} value={t.key}>{t.name}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="two-col">
                    <div className="form-group">
                      <label className="form-label">Audience</label>
                      <select className="form-input" {...register('audience_type', { required: true })}>
                        <option value="">Select audience</option>
                        {AUDIENCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    {audienceType === 'landlord_tenants' && (
                      <div className="form-group">
                        <label className="form-label">Select landlord</label>
                        <select className="form-input" {...register('landlord_id')}>
                          <option value="">All landlords</option>
                          {landlords.map(l => <option key={l.id} value={l.id}>{l.first_name} {l.last_name} — {l.company_name}</option>)}
                        </select>
                      </div>
                    )}
                    {audienceType === 'property_tenants' && (
                      <div className="form-group">
                        <label className="form-label">Select property</label>
                        <select className="form-input" {...register('property_id')}>
                          <option value="">Select property</option>
                          {properties.map(p => <option key={p.id} value={p.id}>{p.name} — {p.city}, {p.state}</option>)}
                        </select>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Subject line</label>
                    <input className="form-input" placeholder="Email subject — merge fields supported"
                      {...register('subject', { required: true })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email body</label>
                    <textarea className="form-input" rows={12}
                      placeholder="Email body — use merge fields like {{first_name}}, {{property_name}}, etc."
                      style={{ fontFamily: 'monospace', fontSize: 13 }}
                      {...register('body', { required: true })} />
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button type="submit" className="btn btn-primary">Save as draft</button>
                    <button type="button" className="btn btn-outline" onClick={() => { setShowForm(false); reset(); }}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Campaigns list */}
          <div className="card">
            <div className="card-header"><div className="card-title">All campaigns</div></div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Name</th><th>Template</th><th>Audience</th><th>Status</th><th>Recipients</th><th>Sent</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {campaigns.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-sub)' }}>No campaigns yet</td></tr>
                  )}
                  {campaigns.map(c => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600 }}>{c.name}</td>
                      <td style={{ fontSize: 12, textTransform: 'capitalize' }}>{c.template_key?.replace('_', ' ')}</td>
                      <td style={{ fontSize: 12, textTransform: 'capitalize' }}>{c.audience_type?.replace('_', ' ')}</td>
                      <td>
                        <span className={`badge badge-${c.status === 'sent' ? 'green' : c.status === 'sending' ? 'blue' : c.status === 'failed' ? 'red' : 'gray'}`}>
                          {c.status}
                        </span>
                      </td>
                      <td>{c.total_recipients || 0}</td>
                      <td style={{ fontSize: 12 }}>{c.sent_at ? fmtDate(c.sent_at) : '—'}</td>
                      <td>
                        {c.status === 'draft' && (
                          <button className="btn btn-primary btn-sm"
                            onClick={() => sendCampaign(c.id)}
                            disabled={sending[c.id]}>
                            {sending[c.id] ? 'Sending...' : '📧 Send now'}
                          </button>
                        )}
                        {c.status === 'sent' && (
                          <span style={{ fontSize: 12, color: 'var(--green)' }}>
                            ✅ {c.total_sent} sent
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </>}
          <LegalFooter />
        </main>
      </div>
    </div>
  );
}
